import type { SavedTextStyle } from "schemas/generated/designLibrary";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import TextSavedStyleModifier from "@v1/components/designLibrary/TextSavedStyleModifier";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import { generateNewName } from "@v1/utils/designLibrary";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { Pencil, Plus, X } from "lucide-react";
import { convertFontWeightToReadableString } from "replo-runtime/shared/utils/font";

import ShopStylePreviewText from "./ShopStylePreviewText";
import TextSavedStyleSkeleton from "./TextSavedStyleSkeleton";

interface TextSavedStyleGroupProps {
  highestHeadingStyle?: SavedTextStyle;
  paragraphStyle?: SavedTextStyle;
  isLoading: boolean;
  createDraftTextStyle: (style: SavedTextStyle) => { id: string };
  updateDraftTextStyle: (style: SavedTextStyle & { id: string }) => void;
  deleteDraftTextStyle: (styleId: string) => void;
  draftStyles: {
    text: (SavedTextStyle & { id: string })[];
  };
}

const TextSavedStyleGroup: React.FC<TextSavedStyleGroupProps> = ({
  highestHeadingStyle,
  paragraphStyle,
  isLoading,
  createDraftTextStyle,
  updateDraftTextStyle,
  deleteDraftTextStyle,
  draftStyles,
}) => {
  const [textStyleBeingEditedId, setTextStyleBeingEditedId] = React.useState<
    string | null
  >(null);
  const [textModifierPopoverIsOpen, setTextModifierPopoverIsOpen] =
    React.useState(false);

  const textSavedStyles = draftStyles?.text ?? [];

  const projectId = useCurrentProjectId();

  const handleNewTextStyle = async () => {
    if (!projectId) {
      return null;
    }

    const newName = generateNewName(textSavedStyles, "text");
    const savedStyleToCreate = {
      name: newName,
      type: "text" as const,
      attributes: {
        type: "text" as const,
        htmlTag: "p" as const,
      },
    };

    const { id } = createDraftTextStyle(savedStyleToCreate);

    setTextModifierPopoverIsOpen(true);
    setTextStyleBeingEditedId(id);
  };

  const selectedTextStyle = textSavedStyles.find(
    (savedStyle) => savedStyle.id === textStyleBeingEditedId,
  );

  return (
    <>
      {selectedTextStyle && (
        <TextSavedStyleModifier
          isPopoverOpen={textModifierPopoverIsOpen}
          onPopoverOpenChange={(isOpen) => {
            if (!isOpen) {
              setTextStyleBeingEditedId(null);
            }
            setTextModifierPopoverIsOpen(isOpen);
          }}
          textSavedStyle={selectedTextStyle}
          handleUpdateTextSavedStyle={(updatedStyle) => {
            return updateDraftTextStyle(updatedStyle);
          }}
          sideOffset={20}
        />
      )}

      <ModifierGroup title="Text Styles" tooltipText="Add new text style">
        <div className="flex flex-col gap-2">
          {isLoading ? (
            <TextSavedStyleSkeleton />
          ) : (
            <>
              {(highestHeadingStyle || paragraphStyle) && (
                <div className="bg-neutral-soft flex w-full flex-col items-center rounded-lg p-2">
                  {highestHeadingStyle && (
                    <ShopStylePreviewText
                      attributes={highestHeadingStyle.attributes}
                      text="Heading"
                      className="text-[20px] leading-[28px]"
                    />
                  )}
                  {paragraphStyle && (
                    <ShopStylePreviewText
                      attributes={paragraphStyle.attributes}
                      text="This is your paragraph."
                      className="text-[12px] leading-[16px]"
                    />
                  )}
                </div>
              )}
              {textSavedStyles.map((savedStyle, index) => (
                <TextStyleRow
                  key={index}
                  savedStyle={savedStyle}
                  editingSavedStyleId={textStyleBeingEditedId}
                  setEditingSavedStyleId={(id) => {
                    setTextModifierPopoverIsOpen(true);
                    setTextStyleBeingEditedId(id);
                  }}
                  handleDeleteTextStyle={(styleId) => {
                    return deleteDraftTextStyle(styleId);
                  }}
                />
              ))}

              <button
                className="hover:bg-neutral-soft flex w-full cursor-pointer items-center gap-2 rounded-md p-1"
                onClick={() => void handleNewTextStyle()}
                aria-label="Add new text style"
              >
                <div className="bg-neutral-soft text-muted border-border border-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border-dashed p-[6px] text-sm font-medium">
                  <Plus size={12} className="text-muted" />
                </div>
                <div className="text-xs font-medium">Add text style</div>
              </button>
            </>
          )}
        </div>
      </ModifierGroup>
    </>
  );
};

export const TextStyleRow: React.FC<{
  savedStyle: SavedTextStyle & { id: string };
  editingSavedStyleId?: string | null;
  setEditingSavedStyleId?: (id: string | null) => void;
  handleDeleteTextStyle?: (styleId: string) => void;
  modificationEnabled?: boolean;
  onClick?: () => void;
}> = ({
  savedStyle,
  editingSavedStyleId,
  setEditingSavedStyleId,
  handleDeleteTextStyle,
  modificationEnabled = true,
  onClick,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const isSelected = editingSavedStyleId === savedStyle.id;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      onMouseOver={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onClick={handleClick}
      className={twMerge(onClick && "cursor-pointer")}
    >
      <div
        className={twMerge(
          "flex w-full items-center gap-2 rounded-sm p-1",
          (isHovered || isSelected) && "bg-neutral-soft",
        )}
      >
        <div className="text-muted border-border border-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white p-[6px] text-sm font-medium">
          {savedStyle.attributes.htmlTag.toUpperCase()}
        </div>
        <div className="flex min-w-0 flex-grow flex-col truncate">
          <div className="truncate text-xs font-medium">{savedStyle.name}</div>
          <div className="text-muted truncate text-xs">
            {[
              savedStyle.attributes.fontFamily,
              savedStyle.attributes.fontSize,
              savedStyle.attributes.fontWeight
                ? convertFontWeightToReadableString(
                    savedStyle.attributes.fontWeight,
                  )
                : undefined,
            ]
              .filter(Boolean)
              .join(" \u00B7 ")}
          </div>
        </div>
        {isHovered && modificationEnabled && (
          <div className="flex gap-1">
            {setEditingSavedStyleId && (
              <DeprecatedIconButton
                icon={<Pencil size={12} />}
                onClick={() => setEditingSavedStyleId(savedStyle.id)}
                variant="tertiary"
                aria-label="Edit style"
                size="sm"
              />
            )}
            {handleDeleteTextStyle && (
              <DeprecatedIconButton
                icon={<X size={12} />}
                onClick={() => handleDeleteTextStyle(savedStyle.id)}
                variant="tertiary"
                aria-label="Delete style"
                size="sm"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextSavedStyleGroup;
